import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

// app: actions
import saveUserSettings from 'actions/user/saveUserSettings';

// app: components
import Heading from 'components/Heading';
import { DietButtonMultiSelect } from 'components/UserSettings/NutritionSettings';
import { OptionMultiSelect } from 'components/UserSettings/CulinarySettings';

// app: constants
import {
  CULINARY_TOPIC_OPTIONS,
  CULINARY_REGION_OPTIONS,
} from 'constants/culinary-interest-options';

// app: services
import { sendEvent } from 'services/analytics';
import {
  PANEL,
  PANEL_EVENT,
  PANEL_IDS,
  parsePanels,
  pendingPanels,
  recordPanelEvent,
  serializePanels,
} from 'services/onboardingPanels';
import {
  onboardingStatusFor,
  preferenceSelection,
  selectionChanged,
} from 'services/onboardingPreferences';

// assets
import CssNutritionSettings from '../UserSettings/NutritionSettings/NutritionSettings.scss';
import CssPreferencesOnboarding from './PreferencesOnboarding.scss';

// Keeps its space in the layout but is out of sight, out of the tab order and out of the a11y tree.
const HIDDEN_STYLE = { visibility: 'hidden' };

const asArray = value => (Array.isArray(value) ? value : []);

export const THANK_YOU_MESSAGE = 'Thank you, your preferences have been updated. Happy cooking!';

// What each panel says. The questions are asked in this order.
const PANEL_CONTENT = {
  [PANEL.DIET]: {
    heading: 'Do you have any dietary preferences?',
    text:
      'This will be used to help order your search results. Update your choices any time via settings.',
  },
  [PANEL.INTERESTS]: {
    heading: 'Which recipes interest you most?',
    text:
      'Pick the categories and regions you enjoy—this will be used to help order your search results. You can update your choices any time via settings.',
  },
};

// The settings as they are right now. The handlers below change them and then read them again in
// the same breath, before the component has had a chance to render the change.
const currentSettings = (getState, fallback) => getState().user.settings || fallback || {};

// Records that the user was asked, and how it went overall, so the next ask can be scheduled - a
// month later, then three months - and only for what is still outstanding.
export const recordPreferencesAsk = (dispatch, settings) =>
  dispatch((innerDispatch, getState) => {
    const latest = currentSettings(getState, settings);
    return innerDispatch(
      saveUserSettings({
        onboarding_preferences_status: onboardingStatusFor(latest),
        onboarding_preferences_asked_at: [
          ...asArray(latest.onboarding_preferences_asked_at),
          new Date().toISOString(),
        ],
      }),
    );
  });

// The timed prompt records the ask the moment it opens, so a crash or a closed tab still counts and
// the next ask is scheduled from the right time...
export const recordPreferencesShown = (dispatch, settings) =>
  dispatch(
    saveUserSettings({
      onboarding_preferences_asked_at: [
        ...asArray(settings?.onboarding_preferences_asked_at),
        new Date().toISOString(),
      ],
    }),
  );

// ...and only notes how it went overall when it is closed.
export const recordPreferencesOutcome = (dispatch, settings) =>
  dispatch((innerDispatch, getState) =>
    innerDispatch(
      saveUserSettings({
        onboarding_preferences_status: onboardingStatusFor(currentSettings(getState, settings)),
      }),
    ),
  );

// Tells analytics (Google Tag Manager on the web, Firebase in the apps) what happened to a panel.
const sendPanelEvent = (panel, event, askNumber) => {
  try {
    sendEvent({
      event: `onboarding_panel_${event}`,
      onboarding_panel: panel,
      onboarding_ask: askNumber,
    });
  } catch (error) {
    console.warn('The onboarding panel event could not be sent', error);
  }
};

/**
 * The preference questions, in the style of the Premium offer, one panel at a time. Used both as a
 * page of its own (the step after the Premium offer) and inside the lightbox for later re-asks.
 * Only the panels not yet completed are shown (all of them if there are none outstanding, as for the
 * test link).
 *
 * Once something is selected, "Continue" takes the place of "Skip for now". Choices are saved as
 * they are made. Each panel is recorded as shown, then skipped (Skip, or closing while it is on
 * screen) or completed (Continue); finishing also records that the user was asked, and calls
 * `onDone`.
 */
function PreferencesFlow({ onDone, recordAsk, recordPanels, control }) {
  useStyles(CssNutritionSettings, CssPreferencesOnboarding);
  const dispatch = useDispatch();
  const settings = useSelector(state => state.user.settings);
  const settingsResolved = useSelector(state => state.user.settingsResolved || false);
  const [step, setStep] = useState(0);
  // What was selected when the questions opened, to tell whether anything changed by the end.
  const initialSelection = useRef(preferenceSelection(settings));

  const dietIds = asArray(settings?.nutrition_diet_prefer);
  const topicIds = asArray(settings?.culinary_topics);
  const regionIds = asArray(settings?.culinary_regions);

  // The panels to show are fixed when the questions open: choosing something must not change them.
  const panelIds = useRef(null);
  if (panelIds.current === null) {
    const pending = pendingPanels(settings);
    panelIds.current = pending.length > 0 ? pending : PANEL_IDS;
  }
  const panels = panelIds.current;
  const panel = panels[step];
  const isLastStep = step === panels.length - 1;
  const hasSelection =
    panel === PANEL.INTERESTS ? topicIds.length + regionIds.length > 0 : dietIds.length > 0;
  const { heading, text } = PANEL_CONTENT[panel];

  // What has been recorded for each panel so far, and the panel on screen that has not been
  // answered yet (so closing the questions can count it as skipped).
  const record = useRef(parsePanels(settings));
  const onScreen = useRef(null);
  const askNumber = Math.max(1, asArray(settings?.onboarding_preferences_asked_at).length);

  // Notes what happened to one or more panels, together: in the account's record, and in analytics.
  const note = changes => {
    if (!recordPanels) {
      return;
    }
    changes.forEach(([id, event]) => {
      record.current = recordPanelEvent(record.current, id, event);
      sendPanelEvent(id, event, askNumber);
    });
    dispatch(saveUserSettings({ onboarding_panels: serializePanels(record.current) }));
  };

  useEffect(() => {
    onScreen.current = panels[0];
    note([[panels[0], PANEL_EVENT.SHOWN]]);
    return () => {
      // Closed (or taken away) with a panel on screen: that panel was skipped. Not when the whole
      // thing is being withdrawn because something else opened over it before it could be seen.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (onScreen.current && !(control && control.current && control.current.discarded)) {
        note([[onScreen.current, PANEL_EVENT.SKIPPED]]);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = () => {
    if (recordAsk) {
      recordPreferencesAsk(dispatch, settings);
    }
    // Thank them, but only if they actually saved a change: not if they skipped everything.
    if (selectionChanged(initialSelection.current, preferenceSelection(settings))) {
      try {
        toast.success(THANK_YOU_MESSAGE);
      } catch (error) {
        console.warn('The thank-you message could not be shown', error);
      }
    }
    onDone();
  };

  // `event` is how the panel on screen was left: completed (Continue) or skipped (Skip for now).
  const handleNext = event => {
    const changes = [[panel, event]];
    if (!isLastStep) {
      changes.push([panels[step + 1], PANEL_EVENT.SHOWN]);
    }
    onScreen.current = isLastStep ? null : panels[step + 1];
    note(changes);
    if (isLastStep) {
      finish();
    } else {
      setStep(step + 1);
    }
  };

  return (
    <div className="preferences-onboarding__card">
      <Heading
        id="preferences-onboarding-heading"
        level={2}
        className="heading--size-32 margin-bottom-10"
      >
        {heading}
      </Heading>
      <p className="preferences-onboarding__intro">{text}</p>
      <div className="preferences-onboarding__selector">
        {panel === PANEL.INTERESTS ? (
          <div>
            <div className="nutrition-settings__group">
              <div className="nutrition-settings__group-label">Categories</div>
              <OptionMultiSelect
                name="Categories"
                options={CULINARY_TOPIC_OPTIONS}
                selectedIds={topicIds}
                onChange={ids => dispatch(saveUserSettings({ culinary_topics: ids }))}
                disabled={!settingsResolved}
              />
            </div>
            <div className="nutrition-settings__group">
              <div className="nutrition-settings__group-label">Regions</div>
              <OptionMultiSelect
                name="Regions"
                options={CULINARY_REGION_OPTIONS}
                selectedIds={regionIds}
                onChange={ids => dispatch(saveUserSettings({ culinary_regions: ids }))}
                disabled={!settingsResolved}
              />
            </div>
          </div>
        ) : (
          <DietButtonMultiSelect
            name="Diet"
            allowNoPreference
            selectedIds={dietIds}
            onChange={ids => dispatch(saveUserSettings({ nutrition_diet_prefer: ids }))}
            disabled={!settingsResolved}
          />
        )}
      </div>
      {/* Both are always rendered, stacked in one cell, so swapping them never moves the layout. */}
      <div className="preferences-onboarding__actions">
        <div
          className="preferences-onboarding__action"
          style={hasSelection ? undefined : HIDDEN_STYLE}
        >
          <Button
            className="preferences-onboarding__continue"
            color="primary"
            size="large"
            variant="contained"
            onClick={() => handleNext(PANEL_EVENT.COMPLETED)}
          >
            Continue
          </Button>
        </div>
        <div
          className="preferences-onboarding__action"
          style={hasSelection ? HIDDEN_STYLE : undefined}
        >
          <button
            type="button"
            onClick={() => handleNext(PANEL_EVENT.SKIPPED)}
            className="link link--energized"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}

PreferencesFlow.propTypes = {
  onDone: PropTypes.func.isRequired,
  // Test links turn this off so that looking at the screens does not use up the user's asks.
  recordAsk: PropTypes.bool,
  // Whether to keep the record of what happened to each panel. Test links turn this off too.
  recordPanels: PropTypes.bool,
  // Lets the popup say the questions were withdrawn before they could be seen (nothing to record).
  control: PropTypes.shape({ current: PropTypes.shape({ discarded: PropTypes.bool }) }),
};

PreferencesFlow.defaultProps = {
  recordAsk: true,
  recordPanels: true,
  control: null,
};

export default PreferencesFlow;
